import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Error } from "@tql/ui-components-react/kendo/labels";
import { useEffect, useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostVoidShipment } from "../../hooks/use-post-void-shipment.ts";
import { CancelDispatchErrorModal } from "../cancel-dispatch-error-modal/index.tsx";

export const CancelShipmentModal = ({
	carrierPhone,
	onClose,
	isOpen,
}: {
	carrierPhone?: string | null;
	onClose: () => void;
	isOpen: boolean;
}) => {
	const confirmationNumberId = useId();
	const errorId = `${confirmationNumberId}_error`;
	const [showError, setShowError] = useState(false);
	const [cancelReasonMessage, setCancelReasonMessage] = useState("");
	const params = useParams();
	const [dispatchModalState, setDispatchModalState] = useState<{
		confirmationNumber: string | null | undefined;
	} | null>(null);
	const { voidShipmentAsync, isSavingVoidShipment } = usePostVoidShipment();

	// Match the 15s auto-close used by toast error notifications elsewhere.
	useEffect(() => {
		if (!showError) return;
		const timer = setTimeout(() => setShowError(false), 15_000);
		return () => clearTimeout(timer);
	}, [showError]);

	const handleChange = (event: InputChangeEvent) => {
		if (showError) {
			setShowError(false);
		}
		setCancelReasonMessage(event.target.value as string);
	};

	const handleConfirm = async () => {
		if (isSavingVoidShipment) {
			return;
		}
		if (!cancelReasonMessage.trim()) {
			setShowError(true);
			return;
		}

		try {
			const resp = await voidShipmentAsync({
				poNumber: Number(params["poNumber"]),
				reason: cancelReasonMessage,
			});
			setCancelReasonMessage("");

			// The carrier dispatch could not be cancelled automatically — surface
			// the cancel-dispatch modal so the user knows to call the carrier.
			if (
				resp.carrierDispatchCancelled === false &&
				resp.shipmentStateAtVoid &&
				resp.shipmentStateAtVoid > 0
			) {
				setDispatchModalState({ confirmationNumber: resp.confirmationNumber });
				return;
			}

			onClose();
		} catch {
			// The void mutation surfaces its own error notification.
			onClose();
		}
	};

	if (!isOpen) {
		return null;
	}

	// The carrier dispatch could not be cancelled automatically — replace the
	// cancel-shipment form with the cancel-dispatch modal so the user knows to
	// call the carrier.
	if (dispatchModalState !== null) {
		return (
			<CancelDispatchErrorModal
				carrierPhone={carrierPhone}
				confirmationNumber={dispatchModalState.confirmationNumber}
				isOpen={true}
				onClose={() => {
					setDispatchModalState(null);
					onClose();
				}}
			/>
		);
	}

	return (
		<TraxModal
			onCancel={() => {
				setCancelReasonMessage("");
				onClose();
			}}
			onClose={() => {
				setCancelReasonMessage("");
				onClose();
			}}
			onConfirm={() => void handleConfirm()}
			title={`Cancel Shipment`}
			type={"confirmation"}
			width={"20%"}
		>
			<div>
				<label
					htmlFor={confirmationNumberId}
					style={{ display: "block", marginBottom: "8px" }}
				>
					Please enter a reason for cancelling this shipment.
					<span className={"required"}>&nbsp;*</span>
				</label>
				<Input
					ariaDescribedBy={showError ? errorId : undefined}
					id={confirmationNumberId}
					onChange={handleChange}
					valid={!showError}
					value={cancelReasonMessage}
				/>
				{showError ? (
					<Error id={errorId}>Cancellation reason is required.</Error>
				) : null}
			</div>
		</TraxModal>
	);
};
